import { Shape, ShapeType } from '@/services/shape';
import { useWatchfaceText } from './use-watchface-text';
import { useLayerStore } from '@/store/use-layer-store';
import { Description } from '@/types/watchface';
import { EventType } from '@/utils/emitter.utils';
import { useEmitterListener } from './use-event-listener';
import { getTextConfig } from '@/utils/shape/text.utils';
import { useWatchfaceStore } from '@/store/use-watchface-store';
import { useShapeManage } from './use-shape-manage';
import { WatchfaceDeviceType } from './use-watchface-devices';
import { canUse } from '@/utils/can-use.utils';

export const getUnSupportShapesByWatchfaceType = (
  newType: Description['watchfaceType']
) => {
  let shapeTypes: ShapeType[] = [];
  switch (newType) {
    case 'normal':
      shapeTypes = [
        ShapeType.PhotoAlbumBg,
        ShapeType.TextWatchface,
        ShapeType.VideoWatchface
      ];
      break;
    case 'textWatchface':
      shapeTypes = [
        ShapeType.PhotoAlbumBg,
        ShapeType.Rive,
        ShapeType.VideoWatchface
      ];
      break;
    case 'portrait':
      shapeTypes = [
        ShapeType.TextWatchface,
        ShapeType.Rive,
        ShapeType.VideoWatchface
      ];
      break;
    case 'photoAlbum':
      shapeTypes = [
        ShapeType.TextWatchface,
        ShapeType.Rive,
        ShapeType.VideoWatchface
      ];
      break;
    case 'videoWatchface':
      shapeTypes = [
        ShapeType.PhotoAlbumBg,
        ShapeType.TextWatchface,
        ShapeType.Rive
      ];
      break;
  }
  return shapeTypes;
};

export const useWatchfaceTypeChange = () => {
  const layerStore = useLayerStore();
  const watchfaceStore = useWatchfaceStore();

  // 表盘类型切换后
  const { getDefaultTexts } = useWatchfaceText();
  const { toCenter } = useShapeManage();

  const delUnSupportShapes = (newType: Description['watchfaceType']) => {
    const shapeTypes = getUnSupportShapesByWatchfaceType(newType);
    // 根据shapeType查找所有shapeId
    const shapeIds: string[] = [];
    layerStore.themes.forEach((theme) => {
      theme.children.forEach((layer) => {
        if (shapeTypes.includes(layer.type as ShapeType))
          shapeIds.push(layer.id);
      });
    });

    // 执行删除操作
    shapeIds.forEach((shapeId) => {
      Shape.getShape(shapeId)?.destroy();
    });
  };

  const addSupportShapes = async (newType: Description['watchfaceType']) => {
    const normalThemes = layerStore.themes.filter(
      (theme) => theme.type == 'normal'
    );
    // 新增类型
    if (newType == 'textWatchface') {
      // 新增文字
      const { fontId, fontWeight } = await getTextConfig();
      normalThemes.forEach((theme) => {
        const shape = Shape.create(ShapeType.TextWatchface, {
          attrs: {
            fontId,
            fontWeight,
            w: 200,
            h: 50,
            textType: 'dateWeek',
            texts: [...getDefaultTexts('dateWeek')],
            recolorEnable: false,
            x: 0,
            y: 0
          },
          theme
        });
        toCenter(shape);
      });
    } else if (newType == 'photoAlbum' || newType == 'portrait') {
      const { width, height } = watchfaceStore.size;
      normalThemes.forEach((theme) => {
        Shape.create(ShapeType.PhotoAlbumBg, {
          attrs: {
            x: 0,
            y: 0,
            w: width,
            h: height
          },
          theme
        });
      });
    } else if (newType == 'videoWatchface') {
      const { width, height } = watchfaceStore.size;
      normalThemes.forEach((theme) => {
        Shape.create(ShapeType.VideoWatchface, {
          attrs: {
            x: 0,
            y: 0,
            w: width,
            h: height
          },
          theme
        });
      });
    }
  };

  useEmitterListener(
    EventType['watchface:type-changed'],
    async (data: { type: Description['watchfaceType'] }) => {
      const { type } = data;
      delUnSupportShapes(type);
      addSupportShapes(type);
    }
  );
};

export const useWathchfaceType = () => {
  const getSupportWatchfaceTypes = (deviceType: WatchfaceDeviceType) => {
    const arr = [
      {
        value: 'normal',
        label: '普通表盘'
      }
    ];
    if (canUse('photoAlbumBg', deviceType).valid) {
      arr.push({
        value: 'photoAlbum',
        label: '相册表盘'
      });
    }
    if (canUse('portraitBg', deviceType).valid) {
      arr.push({
        value: 'portrait',
        label: '人像表盘'
      });
    }
    if (
      canUse('text-watchface', deviceType).valid &&
      canUse('shape-text', deviceType).valid
    ) {
      arr.push({
        value: 'textWatchface',
        label: '文字表盘'
      });
    }
    if (canUse('video-watchface', deviceType).valid) {
      arr.push({
        value: 'videoWatchface',
        label: '视频表盘'
      });
    }
    return arr;
  };

  const correctWathfaceType = (
    deviceType: WatchfaceDeviceType,
    watchfaceType: Description['watchfaceType']
  ) => {
    const supportTypes = getSupportWatchfaceTypes(deviceType).map(
      (item) => item.value
    );
    // 如果当前类型不支持，则使用默认类型
    if (!supportTypes.includes(watchfaceType)) {
      return 'normal';
    } else {
      return watchfaceType;
    }
  };

  return {
    getSupportWatchfaceTypes,
    correctWathfaceType
  };
};
